import { useAccount, useBalance, useContractRead } from 'wagmi'
import { namehash } from 'viem'
import { normalize } from 'viem/ens'
import { useEffect, useMemo, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { IconAlertCircle } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { l1IdSubnetConfig, l1NativeConfig } from '../constants/config'
import blockchainConfig from '../constants/blockchain'
import L1Resolver from '../contracts/PublicResolver.json'
import Snackbar from '../components/Snackbar'
import { useErrorMessages } from '../constants/content'

export const blankAddress = '0x0000000000000000000000000000000000000000'

export const useProvider = () => {
  const { connector } = useAccount()
  const [provider, setProvider] = useState<any>()
  useEffect(() => {
    const getProvider = async () => {
      const prvdr = await connector?.getProvider()
      setProvider(prvdr)
    }
    getProvider()
  }, [connector])
  if (!provider) throw new Error('Provider not found')
  return provider
}

export const useOwnBalance = () => {
  const { t } = useTranslation()
  const errorMessages = useErrorMessages()
  const { address } = useAccount()
  const balance = useBalance({
    address,
    chainId: l1NativeConfig.chainId,
    watch: true,
    onError: error => {
      enqueueSnackbar(
        <Snackbar
          value={errorMessages.betanetConnection}
          title={t('errors.snackbar.fetchBalance', 'Error fetching balance')}
          icon={<IconAlertCircle size="14px" />}
        />,
        { preventDuplicate: true, autoHideDuration: 10000 }
      )
    },
  })
  return balance.data?.formatted || '0'
}

export const useL1nsName = (account?: string) => {
  const { address: wagmiAddress } = useAccount()
  const address = useMemo(() => account || wagmiAddress, [account, wagmiAddress])
  const name = normalize(`${String(address).substring(2)}.addr.reverse`)
  const node = namehash(name)
  const { data, isError, isLoading, isSuccess } = useContractRead({
    chainId: l1IdSubnetConfig.chainId,
    address: blockchainConfig.CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
    abi: L1Resolver.abi,
    functionName: 'name',
    args: [node],
  })
  return { data, isError, isLoading, isSuccess }
}

export const useL1nsAddress = (handle: string) => {
  const name = normalize(`${handle}.l1`)
  const node = namehash(name)
  const { data, isError, isLoading, isSuccess } = useContractRead({
    chainId: l1IdSubnetConfig.chainId,
    address: blockchainConfig.CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
    abi: L1Resolver.abi,
    functionName: 'addr',
    args: [node],
  })
  return { data, isError, isLoading, isSuccess }
}

export const useL1nsNickname = (handle?: string) => {
  const { data: username } = useL1nsName()
  const identity = useMemo<string>(
    () => (handle ? `${handle}.l1` : username) as string,
    [handle, username]
  )
  const name = normalize(identity)
  const node = namehash(name)
  const { data, isError, isLoading, isSuccess } = useContractRead({
    chainId: l1IdSubnetConfig.chainId,
    address: blockchainConfig.CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
    abi: L1Resolver.abi,
    functionName: 'text',
    args: [node, 'displayname'],
  })
  return { data, isError, isLoading, isSuccess }
}
